import React, { useEffect } from "react"
import Bugsnag from "@bugsnag/js"
import BugsnagPluginReact from "@bugsnag/plugin-react"
import { getStaticFeatures, useStaticFeature } from "../hooks/use-feature"
import { Sentry } from "./sentry"

let isReactPage = false
const isE2ETest = process.env.CI_E2E_TEST === "true"
const bugsnagEnabled = !isE2ETest && !!process.env.BUGSNAG_API_KEY

if (bugsnagEnabled) {
  Bugsnag.start({
    apiKey: process.env.BUGSNAG_API_KEY,
    plugins: [new BugsnagPluginReact()],
    appVersion: process.env.SHORT_CIRCLE_SHA1,
    releaseStage: process.env.BUGSNAG_ENV || process.env.NODE_ENV,
    enabledReleaseStages: ["production", "staging", "qa"],
    onError: event => {
      if (!isReactPage) {
        return false
      }

      const error = event.errors[0]

      if (error.errorClass === "InvalidError") {
        const message = event.originalError?.message || event.originalError
        if (
          typeof message === "string" &&
          message.startsWith("Object Not Found Matching Id:")
        ) {
          return false
        }
      }

      // Filter out errors originating from Sentry's own code
      // to prevent Bugsnag from reporting on Sentry's internal error handling
      if (error.stacktrace) {
        const hasSentryFrames = error.stacktrace.some(
          frame =>
            frame.file &&
            (frame.file.includes("@sentry/") ||
              frame.file.includes("node_modules/@sentry/"))
        )
        if (hasSentryFrames) {
          return false
        }
      }

      if (window.analytics) {
        const user = window.app.user

        window.analytics.track("error", {
          path: window.location.pathname.replace(/\d+/g, ":id"),
          context: event.context,
          user_id: user?.id,
          account_id: user?.account_id,
          class_name: error.errorClass
        })
      }

      if (error.errorMessage) {
        const property = error.errorMessage.match(
          /read\sproperty\s'(\w+)'\sof/
        )?.[1]

        if (property) {
          event.groupingHash = `property-access:v1-${property}`
        }
      }

      if (window.FS && window.FS.getCurrentSessionURL) {
        const url = window.FS.getCurrentSessionURL(true)
        event.addMetadata("fullstory", {
          enabled: !!url,
          url
        })
      }

      // Attaches all "static" features (loaded with Frontend::Features module)
      // They'll have their own tab in bugsnag error report
      const features = getStaticFeatures()
      if (features.length > 0) {
        event.addMetadata(
          "features",
          features.reduce((acc, feat) => ({ ...acc, [feat]: true }), {})
        )
      }

      // ALSO report to Sentry (dual reporting)
      if (window.sentryReportingEnabled) {
        Sentry.withScope(scope => {
          // Map Bugsnag context to Sentry
          if (event.context) {
            scope.setTag("context", event.context)
          }

          // Map Bugsnag severity to Sentry level
          if (event.severity) {
            scope.setLevel(event.severity)
          }

          // Map Bugsnag groupingHash to Sentry fingerprint
          if (event.groupingHash) {
            scope.setFingerprint([event.groupingHash])
          }

          // Map all Bugsnag metadata to Sentry contexts
          Object.entries(event.metadata || {}).forEach(([key, value]) => {
            scope.setContext(key, value)
          })

          // Add user info to Sentry
          const user = window.app?.user
          if (user) {
            scope.setUser({
              id: user.id,
              email: user.email
            })
            scope.setContext("account", {
              id: user.account_id
            })
          }

          // Capture the error to Sentry
          // Use originalError if it's an Error instance OR has a stack property
          // (cross-frame compatibility - instanceof can fail across iframes)
          // If neither, reconstruct the stack from Bugsnag's parsed stacktrace
          const originalError = event.originalError
          const isErrorLike =
            originalError instanceof Error ||
            (originalError && typeof originalError.stack === "string")

          let exception
          if (isErrorLike) {
            exception = originalError
          } else {
            // Create a new Error but try to preserve the original stack from Bugsnag
            exception = new Error(error.errorMessage)
            // Bugsnag's error object contains the parsed stacktrace - reconstruct it
            if (error.stacktrace?.length > 0) {
              exception.stack = `${error.errorClass}: ${error.errorMessage}\n${error.stacktrace
                .map(
                  frame =>
                    `    at ${frame.method || "<anonymous>"} (${frame.file}:${frame.lineNumber}:${frame.columnNumber})`
                )
                .join("\n")}`
            }
          }

          // Set the error name to match Bugsnag's errorClass for consistency
          if (error.errorClass && exception.name === "Error") {
            exception.name = error.errorClass
          }

          Sentry.captureException(exception)
        })
      }
    }
  })
}

const ErrorBoundary = bugsnagEnabled
  ? Bugsnag.getPlugin("react").createErrorBoundary(React) // When bugsnag is disabled, this method returns undefined
  : ({ children }) => children

function BugsnagComponent({ children }) {
  isReactPage = true

  const { enabled: sentryReportingEnabled } =
    useStaticFeature("sentry_reporting")

  window.sentryReportingEnabled = sentryReportingEnabled

  useEffect(() => {
    if (!bugsnagEnabled || !window.app) {
      return
    }

    // Set user data in Bugsnag
    const user = window.app.user
    Bugsnag.setUser(user?.id, user?.email, user?.account_id)
    Bugsnag.addMetadata("account", { id: user?.account_id })

    // Also set user in Sentry if initialized
    Sentry.setUser({
      id: user?.id,
      email: user?.email
    })

    Sentry.setContext("account", {
      id: user?.account_id,
      created_at: user?.account_created_at
    })
  }, [])

  return <ErrorBoundary>{children}</ErrorBoundary>
}

export default BugsnagComponent // eslint-disable-line import/no-default-export
